/**
 * Data client for /api/projects (backend mutations) and supabase-js reads
 * (list, detail) — the "hybrid" data path.
 */
import { API } from "@/lib/api";
import { supabase, getAccessToken } from "@/lib/supabaseClient";

async function authHeader() {
    const tok = await getAccessToken();
    return tok ? { Authorization: `Bearer ${tok}` } : {};
}

async function throwWithBody(r, action) {
    let detail;
    const raw = await r.text(); try { detail = JSON.parse(raw); } catch { detail = { message: raw }; }
    const err = new Error(`${action} failed (${r.status})`);
    err.status = r.status;
    err.detail = detail;
    throw err;
}

/** Read all of the caller's projects via supabase-js (RLS-scoped). */
export async function listProjects({ includeArchived = true } = {}) {
    let q = supabase
        .from("projects")
        .select("id, owner_id, name, slug, description, status, created_at, updated_at, archived_at")
        .order("updated_at", { ascending: false });
    if (!includeArchived) q = q.eq("status", "active");
    const { data, error } = await q;
    if (error) throw error;
    return data ?? [];
}

export async function getProject(id) {
    const { data, error } = await supabase
        .from("projects")
        .select("id, owner_id, name, slug, description, status, created_at, updated_at, archived_at")
        .eq("id", id)
        .maybeSingle();
    if (error) throw error;
    return data;
}

export async function createProject({ name, description }) {
    const r = await fetch(`${API}/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        body: JSON.stringify({ name, description: description || null }),
    });
    if (!r.ok) await throwWithBody(r, "createProject");
    return r.json();
}

export async function updateProject(id, patch) {
    const r = await fetch(`${API}/projects/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        body: JSON.stringify(patch),
    });
    if (!r.ok) await throwWithBody(r, "updateProject");
    return r.json();
}

export async function archiveProject(id) {
    const r = await fetch(`${API}/projects/${id}/archive`, {
        method: "POST",
        headers: { ...(await authHeader()) },
    });
    if (!r.ok) await throwWithBody(r, "archiveProject");
    return r.json();
}

export async function unarchiveProject(id) {
    const r = await fetch(`${API}/projects/${id}/unarchive`, {
        method: "POST",
        headers: { ...(await authHeader()) },
    });
    if (!r.ok) await throwWithBody(r, "unarchiveProject");
    return r.json();
}
